/** Sidebar widths per content. The session list's panel opens at 404
 *  px; the admin menu is a list of short links and needs about 270.
 *  They share the collapsed flag, but each keeps its own width, so
 *  resizing one never squeezes the other.
 *
 *  A width is stored only when the user drags (``saveSidebarWidth``), so
 *  an untouched column follows the default when it changes.
 *
 *  The session list's key is new with the panel layout. The old one was
 *  written on every visit, so it holds the old 500 default for nearly
 *  everyone; that value can't be told from no choice and is dropped.
 *  Any other width in it was dragged to, and moves to the new key once
 *  (``migrateLegacySessionsWidth``). */

export type SidebarMode = "sessions" | "admin";

type WidthSpec = { key: string; initial: number; min: number; max: number };

export const SIDEBAR_WIDTHS: Record<SidebarMode, WidthSpec> = {
  sessions: { key: "repoworks.sidebar.panel.width", initial: 404, min: 280, max: 720 },
  admin: { key: "repoworks.sidebar.admin.width", initial: 280, min: 232, max: 400 },
};

/** The session list's key before the panel layout, and its default. */
export const LEGACY_SESSIONS_WIDTH = { key: "repoworks.sidebar.width", initial: 500 } as const;

/** Move a dragged width from the old session-list key to the new one,
 *  unless the new key already holds a choice, and remove the old key. */
function migrateLegacySessionsWidth(): void {
  const spec = SIDEBAR_WIDTHS.sessions;
  const legacy = localStorage.getItem(LEGACY_SESSIONS_WIDTH.key);
  if (legacy === null) return;
  const raw = legacy === "" ? Number.NaN : Number(legacy);
  if (
    localStorage.getItem(spec.key) === null &&
    Number.isFinite(raw) &&
    raw !== LEGACY_SESSIONS_WIDTH.initial &&
    raw >= spec.min &&
    raw <= spec.max
  ) {
    localStorage.setItem(spec.key, String(raw));
  }
  localStorage.removeItem(LEGACY_SESSIONS_WIDTH.key);
}

/** The stored width for a mode, or its default when nothing usable is
 *  stored (out of range, not a number, storage blocked). */
export function readSidebarWidth(mode: SidebarMode): number {
  const spec = SIDEBAR_WIDTHS[mode];
  try {
    if (mode === "sessions") migrateLegacySessionsWidth();
    const stored = localStorage.getItem(spec.key);
    const raw = stored === null || stored === "" ? Number.NaN : Number(stored);
    if (Number.isFinite(raw) && raw >= spec.min && raw <= spec.max) return raw;
  } catch {
    /* ignore */
  }
  return spec.initial;
}

export function clampSidebarWidth(mode: SidebarMode, width: number): number {
  const spec = SIDEBAR_WIDTHS[mode];
  return Math.min(spec.max, Math.max(spec.min, width));
}

/** Keep a width the user dragged to. Only a drag calls this. */
export function saveSidebarWidth(mode: SidebarMode, width: number): void {
  try {
    localStorage.setItem(SIDEBAR_WIDTHS[mode].key, String(clampSidebarWidth(mode, width)));
  } catch {
    /* storage blocked: the width lasts for this page only */
  }
}
